import React from 'react'
import PropTypes from 'prop-types'
import InputFieldWithBtn from '../shared/input-field-with-btn'
import glamorous from 'glamorous'
import isEmailValid from '../../utils/validate-email'
import { withApollo } from 'react-apollo'
import CalendarInput from '../shared/calendar/calendarInput'
import { flowRight as compose } from 'lodash'
import {
  MessageFormat,
  CurrencyFormat,
  DateTimeFormat,
} from '@paypalcorp/worldready'
import {
  Message,
  ContextConsumer as WorldReadyContextConsumer,
} from '@paypalcorp/worldready-react'

const Item = glamorous.div({
  paddingTop: '12px',
})

const Img = glamorous.img({
  width: '100px',
  borderRadius: '4px',
  backgroundColor: '#ffffff',
  boxShadow: '2px 3px 3px 0 #9b9b9b',
})

const RightCol = glamorous.div({
  textAlign: 'right',
  '@media only screen and (max-width: 768px)': {
    position: 'absolute',
  },
})

const Line = glamorous.div({
  display: 'block',
  height: '1px',
  border: '0',
  borderTop: '1px solid #ccc',
  margin: '12px 0 22px 0',
  padding: '0',
  backgroundColor: '#e7ebed',
})

const ChangeDelivery = glamorous.div({
  height: '48px',
  width: '100%',
  display: 'inline-block',
  borderRadius: '5px',
})

const DeliveryMethod = glamorous.div(
  {
    paddingBottom: '4px',
    color: '#4a4a4a',
    fontSize: '15px',
  },
  'vx_text-body-md',
)

const ChangeBtn = glamorous.button(
  {
    fontSize: '13px',
    fontWeight: '500',
    color: '#0070ba',
    ':focus': {
      outline: 'none',
    },
  },
  'vx_btn-link',
)

const RemoveBtn = glamorous.button(
  {
    textAlign: 'right',
    marginRight: '20px',
    width: '100%',
    fontSize: '13px',
    fontWeight: '500',
    color: '#0070ba',
    ':focus': {
      outline: 'none',
    },
  },
  'vx_btn-link',
)

const ItemName = glamorous.div({
  fontSize: '13px',
  color: '#4a4a4a',
  paddingTop: '12px',
})

const Discount = glamorous.div({
  fontSize: '13px',
  color: '#4a4a4a',
  paddingTop: '6px',
  paddingBottom: '24px',
})

class GiftCardItem extends React.Component {
  static propTypes = {
    idx: PropTypes.number.isRequired,
    item: PropTypes.object.isRequired,
    onRemoveItem: PropTypes.func.isRequired,
    onUpdateItem: PropTypes.func.isRequired,
  }

  state = {
    changeDelivery: false,
    showCalendarInput: false,
  }

  formatDisplayAmount = (item, worldready) => {
    const toPayValue = (item.amountToPay.value * item.quantity) / 100
    const amountToPay = new CurrencyFormat(worldready, {
      currency: item.amountToPay.currency_code,
    }).format(toPayValue)

    const totalSingleAmount = new CurrencyFormat(worldready, {
      currency: item.totalAmount.currency_code,
    }).format(item.totalAmount.value / 100)

    const totalValue = (item.totalAmount.value * item.quantity) / 100
    const totalAmount = new CurrencyFormat(worldready, {
      currency: item.totalAmount.currency_code,
    }).format(totalValue)

    return { amountToPay, totalAmount, totalSingleAmount }
  }

  renderItem = (displayAmount) => {
    const { totalAmount, amountToPay, totalSingleAmount } = displayAmount
    return (
      <Item id="item" className="row">
        <div className="col-md-6" id="item">
          <Img src={this.props.item.imgUrl} alt={this.props.item.productName} />
          <ItemName id="name">
            {totalSingleAmount} {this.props.item.productName}
          </ItemName>
          {this.props.item.discounts.map((discount, idx) => {
            return (
              <Discount key={idx} id="discount">
                {discount.name}
              </Discount>
            )
          })}
        </div>
        <RightCol className="col-md-6" style={{ marginTop: '3px' }}>
          {totalAmount === amountToPay
            ? this.renderItemAmount(totalAmount)
            : this.renderItemAmountWithDiscount(totalAmount, amountToPay)}
        </RightCol>
      </Item>
    )
  }

  renderItemAmount = (totalAmount) => {
    return (
      <div>
        <Message id="pages/cart-summary.cartsummary.quantity" />
        {this.props.item.quantity}
        <span style={{ marginLeft: '15px' }}>{totalAmount}</span>
      </div>
    )
  }

  renderItemAmountWithDiscount = (totalAmount, amountToPay) => {
    return (
      <div>
        <div>
          <Message id="pages/cart-summary.cartsummary.quantity" />
          {this.props.item.quantity}
          <span style={{ marginLeft: '15px', textDecoration: 'line-through' }}>
            {totalAmount}
          </span>
        </div>
        <div style={{ color: '#d20000', paddingTop: '6px' }}>{amountToPay}</div>
      </div>
    )
  }

  // Change delivery input field
  renderChangeDelivery = () => {
    return (
      <WorldReadyContextConsumer>
        {(worldready) => (
          <div>
            <ChangeDelivery id="change-delivery-method">
              <InputFieldWithBtn
                id="delivery-method"
                style={{ flex: 1 }}
                title={new MessageFormat(worldready, {
                  id: 'pages/cart-summary.cartsummary.delivery',
                }).format()}
                inputDefaultValue={this.props.item.recipientEmail}
                handleBtnClick={(value) => this.handleUpdateEmail(value)}
                suppressErrorMsg={this.suppressErrorMsg}
                shouldApplyOnBlur={false}
              />
            </ChangeDelivery>
            {this.state.invalidEmail && (
              <div style={{ color: '#d20000' }}>
                <span className=" vx_icon vx_icon-medium vx_icon-critical-xsmall" />
                <Message id="pages/cart-summary.cartsummary.invalidEmail" />
              </div>
            )}
          </div>
        )}
      </WorldReadyContextConsumer>
    )
  }

  handleUpdateEmail = (value) => {
    if (!isEmailValid(value)) {
      this.setState({ invalidEmail: true })
      return
    }

    this.setState({ recipientEmail: value })
    this.setState({ changeDelivery: false })

    // update date for this item and store in cache
    this.props.item.recipientEmail = value
    this.props.onUpdateItem(this.props.item, this.props.idx)
  }

  suppressErrorMsg = () => {
    this.setState({ invalidEmail: false })
  }

  renderDelivery = () => {
    if (this.props.item.isGifting) {
      return this.renderEmailWithChangeBtn()
    }
    return this.renderEmailWithText()
  }

  renderEmailWithChangeBtn = () => {
    return (
      <div id="delivery" className="row">
        <div className="col-md-8">
          <DeliveryMethod>
            <Message id="pages/cart-summary.cartsummary.delivery" />
          </DeliveryMethod>
          {this.renderEmail()}
        </div>
        <RightCol className="col-md-4">
          <ChangeBtn onClick={this.handleChangeEmail}>
            <Message id="pages/cart-summary.cartsummary.change" />
          </ChangeBtn>
        </RightCol>
      </div>
    )
  }
  renderEmailWithText = () => {
    return (
      <div id="delivery">
        <DeliveryMethod>
          <Message id="pages/cart-summary.cartsummary.delivery" />
        </DeliveryMethod>
        <div style={{ fontSize: '13px', color: '#9b9b9b' }}>
          <Message id="pages/cart-summary.cartsummary.deliveryToMe" />
        </div>
      </div>
    )
  }

  renderEmail = () => {
    return (
      <div
        className="vx_text-body-sm"
        style={{ fontSize: '13px', color: '#9b9b9b' }}
      >
        {this.state.recipientEmail || this.props.item.recipientEmail}
      </div>
    )
  }

  renderDate = (worldready) => {
    const selectedDay = this.state.selectedDay || this.props.item.selectedDay
    const dateFormatter = new DateTimeFormat(worldready)
    const formattedDate = dateFormatter.format(new Date(selectedDay))
    const today = dateFormatter.format(new Date())

    return (
      <div>
        {this.state.showCalendarInput ? (
          <div id="calendar-input" style={{ marginTop: '24px' }}>
            <CalendarInput
              handleDayClick={this.handleDayClick}
              handleOnBlurCalendar={this.handleOnBlurCalendar}
              selectedDay={formattedDate}
            />
          </div>
        ) : (
          this.renderDateDisplay(formattedDate, today)
        )}
      </div>
    )
  }

  renderDateDisplay = (formattedDate, today) => (
    <div id="date" className="row" style={{ marginTop: '24px' }}>
      <div className="col-md-8">
        <div
          className="vx_text-body-md"
          style={{ paddingBottom: '4px', color: '#4a4a4a', fontSize: '15px' }}
        >
          <Message id="pages/cart-summary.cartsummary.date" />
        </div>
        <div
          className="vx_text-body-sm"
          style={{ fontSize: '13px', color: '#9b9b9b' }}
        >
          {formattedDate === today ? (
            <Message id="pages/cart-summary.cartsummary.defaultDate" />
          ) : (
            formattedDate
          )}
        </div>
      </div>
      {this.props.item.isGifting && (
        <RightCol className="col-md-4">
          <ChangeBtn className="vx_btn-link" onClick={this.handleChangeDate}>
            <Message id="pages/cart-summary.cartsummary.change" />
          </ChangeBtn>
        </RightCol>
      )}
    </div>
  )

  renderRemoveBtn = () => {
    return (
      <Item id="remove-button">
        <RemoveBtn className="vx_btn-link" onClick={this.handleRemoveItem}>
          <Message id="pages/cart-summary.cartsummary.remove" />
        </RemoveBtn>
      </Item>
    )
  }

  handleOnBlurCalendar = () => {
    this.setState({ showCalendarInput: false })
  }

  handleDayClick = (date, { disabled }) => {
    if (disabled) {
      return
    }

    this.setState({ showCalendarInput: false })
    this.setState({ selectedDay: date.toString() })

    // update date for this item and store in cache
    this.props.item.selectedDay = date.toString()
    this.props.onUpdateItem(this.props.item, this.props.idx)
  }

  handleChangeEmail = () => {
    this.setState({ changeDelivery: true })
  }

  handleChangeDate = () => {
    this.setState({ showCalendarInput: true })
  }

  handleRemoveItem = () => {
    this.props.onRemoveItem()
  }

  render() {
    return (
      <WorldReadyContextConsumer>
        {(worldready) => (
          <div>
            {this.renderItem(
              this.formatDisplayAmount(this.props.item, worldready),
            )}
            {this.state.changeDelivery
              ? this.renderChangeDelivery()
              : this.renderDelivery()}
            {this.renderDate(worldready)}
            {this.renderRemoveBtn()}
            <Line />
          </div>
        )}
      </WorldReadyContextConsumer>
    )
  }
}

const GiftCardItemWithApollo = compose(withApollo)(GiftCardItem)

export default GiftCardItemWithApollo
